import { createSelector } from '@reduxjs/toolkit';
import { fireFocusEventToSite, firePricingEventToSite } from '@ff-os/pdp-shared-redux/global-state/site-events';
import { observeStore } from '@ff-os/pdp-shared-redux/observeStore';
import { selectAreFontsReady } from '@ff-os/pdp-shared-redux/slices/fontsSlice';
import { selectActiveVariantId, selectActiveVariantSku } from '@ff-scene-plugins/gift-redux/selectors/variantSelectors';
import { selectAllVariables } from '@ff-scene-plugins/gift-redux/slices/variablesSlice';
import { GiftPdpStore } from '@ff-scene-plugins/gift-redux/store';
import { updateFontMetrics } from '@gift-variable-plugins/text-pdp/withFontMetricsUpdate';
import { updateActiveVariantId } from '../_redux-meta';
import { applyDefaultPrefabForActiveVariant } from '../helpers/applyDefaultPrefabForActiveVariant';
import { addDesignChangeListener } from './addDesignChangeListener';
import { ensureFontCompatibility } from './ensureFontCompatibility';

export const subscribe = (store: GiftPdpStore) => {
    trackActiveVariantId(store);
    ensureFontCompatibility(store);
    updateFontMetricsOnceFontsHaveLoaded(store);
    fireFocusEventOnDesignChange(store);
    firePricingEventOnChange(store);
};

const trackActiveVariantId = (store: GiftPdpStore) => {
    observeStore(store, selectActiveVariantId, (activeVariantId) => {
        store.dispatch(updateActiveVariantId(activeVariantId));
        applyDefaultPrefabForActiveVariant(store.dispatch, store.getState);
    });
};

const updateFontMetricsOnceFontsHaveLoaded = (store: GiftPdpStore) => {
    observeStore(store, selectAreFontsReady, (areFontsReady) => {
        if (areFontsReady) {

            const state = store.getState();

            const variables = selectAllVariables(state);
            const textVariables = variables
                .filter(v => v.pluginId === 'text')
                .map(v => v.id);

            updateFontMetrics(store.dispatch, textVariables, state);
        }
    });
};

const fireFocusEventOnDesignChange = (store: GiftPdpStore) => {
    addDesignChangeListener(store, () => {
        fireFocusEventToSite({ name: 'focus' });
    });
};

const firePricingEventOnChange = (store: GiftPdpStore) => {
    observeStore(store, selectPricingEventObservables, ([sku, quantity]) => {
        firePricingEventToSite({
            name: 'pricing',
            items: [{ sku, quantity }]
        });
    });
};

const selectPricingEventObservables = createSelector([
    selectActiveVariantSku,
    () => 1
], (sku, quantity) => [sku, quantity]);